import { amountScale, parseRateInput, toBaseAmount } from '@familywallet/shared';
import type { FieldError, UseFormRegisterReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { formatAmount, formatRateValue } from '../lib/money';
import { needsOneOffRate, parseOpening } from '../lib/opening';
import { Field } from './ui/field';
import { Input } from './ui/input';

interface Props {
  label: string;
  currency: string;
  baseCurrency: string;
  latestRates: Readonly<Record<string, string>>;
  amountValue: string;
  rateValue: string;
  amountField: UseFormRegisterReturn;
  rateField: UseFormRegisterReturn;
  amountError?: FieldError;
  rateError?: FieldError;
}

/**
 * Opening balance in any currency, with the live base-currency value under it ("≈ 5.59 US$") and
 * a one-off rate field when the family has no rate for that currency yet.
 */
export function OpeningBalanceFields(props: Props) {
  const { t, i18n } = useTranslation();
  const { currency, baseCurrency, latestRates } = props;
  const whole = amountScale(currency) === 0;
  const amount = parseOpening(props.amountValue, currency);
  const askRate = needsOneOffRate(currency, amount, baseCurrency, latestRates);
  const rate = askRate ? parseRateInput(props.rateValue) : (latestRates[currency] ?? null);
  const equivalent =
    currency !== baseCurrency && amount && rate
      ? formatAmount(toBaseAmount(amount, rate), baseCurrency, i18n.language)
      : null;
  return (
    <>
      <div className="flex flex-col gap-1.5">
        <Field
          label={props.label}
          hint={whole ? t('amounts.fastEntryHint') : t('accountForm.openingBalanceHint')}
        >
          <Input
            inputMode={whole ? 'text' : 'decimal'}
            autoComplete="off"
            dir="ltr"
            placeholder={whole ? '500k' : '0.00'}
            {...props.amountField}
          />
        </Field>
        {amount && (
          <p className="text-sm text-muted" aria-live="polite">
            <span dir="ltr" className="tabular-nums">
              {formatAmount(amount, currency, i18n.language)}
              {equivalent && ` ≈ ${equivalent}`}
            </span>
          </p>
        )}
        {props.amountError && (
          <p className="text-sm text-danger">
            {props.amountError.message === 'whole'
              ? t('amounts.wholeOnly', { currency })
              : t('accountForm.invalidAmount')}
          </p>
        )}
      </div>
      {askRate && (
        <Field
          label={t('rates.oneOffLabel', { base: baseCurrency, quote: currency })}
          hint={t('rates.oneOffHint')}
        >
          <Input
            inputMode="decimal"
            autoComplete="off"
            dir="ltr"
            placeholder="89,500"
            {...props.rateField}
          />
        </Field>
      )}
      {props.rateError && askRate && (
        <p className="text-sm text-danger">{t('rates.invalidRate')}</p>
      )}
      {!askRate && currency !== baseCurrency && latestRates[currency] && (
        <p className="text-xs text-muted">
          {t('rates.usesFamilyRate', {
            base: baseCurrency,
            rate: formatRateValue(latestRates[currency], i18n.language),
            quote: currency,
          })}
        </p>
      )}
    </>
  );
}
